import {
  DEFAULT_PRESET,
  DEFAULT_SESSION_SETTINGS,
  DEFAULT_VOICE,
  type PresetId,
  type SessionSettings,
} from "@mantra/shared";
import { v7 as uuidv7 } from "uuid";
import { db, kvGet, kvSet, type LocalAffirmation, type LocalSet, type Profile } from "./db";
import { scheduleSync } from "./sync";

/** Монотонное время: LWW на сервере требует, чтобы каждая правка была строго новее предыдущей */
let lastTs = 0;
export function stamp(prev?: string): string {
  let t = Math.max(Date.now(), lastTs + 1);
  if (prev) t = Math.max(t, Date.parse(prev) + 1);
  lastTs = t;
  return new Date(t).toISOString();
}

export interface Draft {
  title: string;
  sourceText: string | null;
  preset: PresetId;
  voiceId?: string;
  settings?: SessionSettings;
  texts: string[];
}

export async function createSet(d: Draft): Promise<string> {
  const id = uuidv7();
  const t = stamp();
  const set: LocalSet = {
    id,
    title: d.title.trim() || "Без названия",
    sourceText: d.sourceText,
    voiceId: d.voiceId ?? DEFAULT_VOICE,
    preset: d.preset,
    settings: d.settings ?? DEFAULT_SESSION_SETTINGS,
    createdAt: t,
    updatedAt: t,
    deletedAt: null,
    dirty: 1,
  };
  const affs: LocalAffirmation[] = d.texts.map((text, position) => ({
    id: uuidv7(),
    setId: id,
    position,
    text: text.trim(),
    createdAt: t,
    updatedAt: t,
    deletedAt: null,
    dirty: 1,
  }));
  await db.transaction("rw", db.sets, db.affirmations, async () => {
    await db.sets.add(set);
    await db.affirmations.bulkAdd(affs);
  });
  scheduleSync();
  return id;
}

export async function updateSet(id: string, patch: Partial<Pick<LocalSet, "title" | "preset" | "voiceId" | "settings" | "sourceText">>) {
  const cur = await db.sets.get(id);
  if (!cur) return;
  await db.sets.put({ ...cur, ...patch, updatedAt: stamp(cur.updatedAt), dirty: 1 });
  scheduleSync();
}

/** Заменить список фраз: совпадающие позиции обновляем, лишние — надгробия */
export async function replaceAffirmations(setId: string, texts: string[]) {
  await db.transaction("rw", db.affirmations, async () => {
    const existing = (await db.affirmations.where("setId").equals(setId).toArray())
      .filter((a) => !a.deletedAt)
      .sort((a, b) => a.position - b.position);
    const clean = texts.map((t) => t.trim()).filter(Boolean);
    for (let i = 0; i < Math.max(existing.length, clean.length); i++) {
      const cur = existing[i];
      const text = clean[i];
      if (cur && text !== undefined) {
        if (cur.text !== text || cur.position !== i)
          await db.affirmations.put({ ...cur, text, position: i, updatedAt: stamp(cur.updatedAt), dirty: 1 });
      } else if (cur) {
        const t = stamp(cur.updatedAt);
        await db.affirmations.put({ ...cur, deletedAt: t, updatedAt: t, dirty: 1 });
      } else if (text !== undefined) {
        const t = stamp();
        await db.affirmations.add({ id: uuidv7(), setId, position: i, text, createdAt: t, updatedAt: t, deletedAt: null, dirty: 1 });
      }
    }
  });
  scheduleSync();
}

export async function deleteSet(id: string) {
  await db.transaction("rw", db.sets, db.affirmations, async () => {
    const cur = await db.sets.get(id);
    if (!cur) return;
    const t = stamp(cur.updatedAt);
    await db.sets.put({ ...cur, deletedAt: t, updatedAt: t, dirty: 1 });
    const affs = await db.affirmations.where("setId").equals(id).toArray();
    await db.affirmations.bulkPut(affs.filter((a) => !a.deletedAt).map((a) => ({ ...a, deletedAt: t, updatedAt: t, dirty: 1 as const })));
  });
  scheduleSync();
}

export async function liveAffirmations(setId: string): Promise<LocalAffirmation[]> {
  return (await db.affirmations.where("setId").equals(setId).toArray())
    .filter((a) => !a.deletedAt)
    .sort((a, b) => a.position - b.position);
}

export async function logPractice(setId: string | null, startedAt: Date, durationSec: number, completed: boolean) {
  if (durationSec < 5) return;
  await db.practice.add({ id: uuidv7(), setId, startedAt: startedAt.toISOString(), durationSec: Math.round(durationSec), completed, dirty: 1 });
  scheduleSync();
}

export const DEFAULT_PROFILE: Profile = { gender: "n", defaultPreset: DEFAULT_PRESET };

export async function getProfile(): Promise<Profile> {
  return { ...DEFAULT_PROFILE, ...((await kvGet<Profile>("profile")) ?? {}) };
}

export async function saveProfile(p: Profile) {
  await kvSet("profile", p);
  await kvSet("profileDirty", true);
  scheduleSync();
}

/** Локальная статистика: работает офлайн, совпадает с серверной формулой */
export async function localStats() {
  const all = await db.practice.toArray();
  const minutes = Math.round(all.reduce((s, p) => s + p.durationSec, 0) / 60);
  const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const days = new Set(all.map((p) => dayKey(new Date(p.startedAt))));
  let streak = 0;
  const d = new Date();
  if (!days.has(dayKey(d))) d.setDate(d.getDate() - 1);
  while (days.has(dayKey(d))) {
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return { sessions: all.length, minutes, streak };
}
